import React, { useState, useEffect } from 'react';
import './PurchaseList.css';

const PurchaseList = () => {
  const [requests, setRequests] = useState([]);
  const [locations, setLocations] = useState([]);
  const [userLocation, setUserLocation] = useState('');
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('CONFIRMED');
  const [locationFilter, setLocationFilter] = useState('');
  const [dateFilter, setDateFilter] = useState({
    startDate: '',
    endDate: ''
  });
  const [printData, setPrintData] = useState(null);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [showCommentsModal, setShowCommentsModal] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        // Récupérer toutes les demandes et les localisations
        const [requestsRes, locationsRes] = await Promise.all([
          fetch('/api/hygiene/requests'),
          fetch('/api/locations/getAllLocations')
        ]);
        
        const requestsData = await requestsRes.json();
        const locationsData = await locationsRes.json();
        
        setRequests(requestsData);
        setLocations(locationsData);
      } catch (error) {
        console.error('Erreur chargement données:', error);
      } finally {
        setLoading(false);
      }
    };

    // Récupérer la localisation de l'utilisateur connecté
    const userLoc = localStorage.getItem('userLocation') || '';
    setUserLocation(userLoc);
    // Si l'utilisateur a une localisation, l'utiliser comme filtre par défaut
    if (userLoc) {
      setLocationFilter(userLoc);
    }

    fetchData();
  }, []);

  // Fonction pour ouvrir la popup des commentaires
  const openCommentsModal = (request) => {
    setSelectedRequest(request);
    setShowCommentsModal(true);
  };

  // Fonction pour fermer la popup
  const closeCommentsModal = () => {
    setShowCommentsModal(false);
    setSelectedRequest(null);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleDateString('fr-FR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      return 'N/A';
    }
  };

  const formatDateForInput = (dateString) => {
    if (!dateString) return '';
    try {
      return new Date(dateString).toISOString().split('T')[0];
    } catch (error) {
      return '';
    }
  };

  const getAdminDate = (request) => {
    // CORRECTION: Utiliser confirmedAt pour la date admin
    if (request.confirmedAt) {
      return request.confirmedAt;
    }
    if (request.status === 'CONFIRMED' && request.updatedAt) {
      return request.updatedAt;
    }
    return null;
  };

  const getControllerDate = (request) => {
    // CORRECTION: Utiliser approvedAt pour la date contrôleur
    if (request.approvedAt) {
      return request.approvedAt;
    }
    if ((request.status === 'APPROVED' || request.status === 'REJECTED') && request.updatedAt) {
      return request.updatedAt;
    }
    return request.updatedAt;
  };

  const getStatusLabel = (status) => {
    const statusLabels = {
      'PENDING': 'En Attente',
      'APPROVED': 'Approuvée',
      'REJECTED': 'Rejetée',
      'CONFIRMED': 'Confirmée'
    };
    return statusLabels[status] || status;
  };

  const getStatusClass = (status) => {
    const statusClasses = {
      'PENDING': 'status-pending',
      'APPROVED': 'status-approved',
      'REJECTED': 'status-rejected',
      'CONFIRMED': 'status-confirmed'
    };
    return statusClasses[status] || 'status-pending';
  };

  const filterByDateRange = (request, startDate, endDate) => {
    if (!startDate && !endDate) return true;
    
    const requestDate = new Date(request.createdAt);
    const start = startDate ? new Date(startDate) : new Date('1970-01-01');
    const end = endDate ? new Date(endDate) : new Date();
    
    // Ajouter un jour à la date de fin pour inclure toute la journée
    end.setDate(end.getDate() + 1);
    
    return requestDate >= start && requestDate < end;
  };

  // Filtrer les demandes
  const filteredRequests = requests.filter(request => {
    // Filtre par localisation
    const locationMatch = locationFilter ? request.location === locationFilter : true;
    
    // Filtre par statut
    const statusMatch = statusFilter ? request.status === statusFilter : true;
    
    // Filtre par date (basé sur la date de création)
    const dateMatch = filterByDateRange(
      request, 
      dateFilter.startDate, 
      dateFilter.endDate
    );
    
    return locationMatch && statusMatch && dateMatch;
  });

  const totalCost = filteredRequests.reduce((total, req) => {
    return total + (req.price || 0) * req.quantity;
  }, 0);

  const getStatusStats = () => {
    const stats = {
      PENDING: 0,
      APPROVED: 0,
      REJECTED: 0,
      CONFIRMED: 0,
      TOTAL: requests.length
    };
    
    requests.forEach(request => {
      if (stats.hasOwnProperty(request.status)) {
        stats[request.status]++;
      }
    });
    
    return stats;
  };

  const clearFilters = () => {
    setStatusFilter(userLocation ? 'CONFIRMED' : '');
    setLocationFilter(userLocation || '');
    setDateFilter({
      startDate: '',
      endDate: ''
    });
  };

  // Fonction pour préparer l'impression par localisation
  const preparePrint = () => {
    // Filtrer seulement les demandes APPROUVÉES
    const approvedRequests = requests.filter(request => request.status === 'APPROVED');
    
    // Grouper par localisation
    const requestsByLocation = approvedRequests.reduce((acc, request) => {
      if (!acc[request.location]) {
        acc[request.location] = [];
      }
      acc[request.location].push(request);
      return acc;
    }, {});

    setPrintData(requestsByLocation);
    
    // Attendre un peu pour que l'état se mette à jour puis imprimer
    setTimeout(() => {
      window.print();
      // Réinitialiser après impression
      setTimeout(() => setPrintData(null), 1000);
    }, 500);
  };

  const statusStats = getStatusStats();

  if (loading) {
    return (
      <div className="purchase-container">
        <div className="purchase-header">
          <h2>Liste des Demandes {userLocation && `- ${userLocation}`}</h2>
        </div>
        <div className="empty-state">
          <div className="loading-spinner"></div>
          <p>Chargement des demandes...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="purchase-container">
      {/* Popup des commentaires - CORRIGÉE */}
      {showCommentsModal && selectedRequest && (
        <div className="modal-overlay" onClick={closeCommentsModal}>
          <div className="comments-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Commentaires - {selectedRequest.productName}</h3>
              <button className="close-btn" onClick={closeCommentsModal}>×</button>
            </div>
            
            <div className="modal-content">
              {/* Commentaire de l'utilisateur */}
              <div className="comment-section">
                <div className="comment-header">
                  <span className="comment-author">Utilisateur ({selectedRequest.requestedBy})</span>
                  <span className="comment-date">
                    {formatDate(selectedRequest.createdAt)}
                  </span>
                </div>
                <div className="comment-content">
                  {selectedRequest.comments ? (
                    selectedRequest.comments
                  ) : (
                    <span className="no-comment">Aucun commentaire</span>
                  )}
                </div>
              </div>

              {/* Commentaire du contrôleur */}
              <div className="comment-section">
                <div className="comment-header">
                  <span className="comment-author">
                    Contrôleur ({selectedRequest.approvedBy || 'Non spécifié'})
                  </span>
                  <span className="comment-date">
                    {formatDate(getControllerDate(selectedRequest))}
                  </span>
                </div>
                <div className="comment-content">
                  {selectedRequest.modificationReason ? (
                    selectedRequest.modificationReason
                  ) : (
                    <span className="no-comment">Aucun commentaire</span>
                  )}
                </div>
              </div>

              {/* Commentaire de l'admin - CORRECTION: Utiliser adminComment */}
              <div className="comment-section">
                <div className="comment-header">
                  <span className="comment-author">
                    Admin ({selectedRequest.confirmedBy || 'Non spécifié'})
                  </span>
                  <span className="comment-date">
                    {formatDate(getAdminDate(selectedRequest))}
                  </span>
                </div>
                <div className="comment-content">
                  {selectedRequest.adminComment ? (
                    selectedRequest.adminComment
                  ) : (
                    <span className="no-comment">Aucun commentaire</span>
                  )}
                </div>
              </div>
            </div>

            <div className="modal-footer">
              <button className="close-modal-btn" onClick={closeCommentsModal}>
                Fermer
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Section d'impression (cachée à l'écran, visible à l'impression) */}
      {printData && (
        <div className="print-section">
          {Object.entries(printData).map(([location, locationRequests]) => (
            <div key={location} className="print-page">
              <div className="print-header">
                <h1>Liste d'Achat - {location}</h1>
                <div className="print-subtitle">Demandes Approuvées</div>
                <div className="print-date">
                  Généré le: {new Date().toLocaleDateString('fr-FR')}
                </div>
              </div>
              
              <table className="print-table">
                <thead>
                  <tr>
                    <th>Produit</th>
                    <th>Quantité</th>
                    <th>Prix Unitaire</th>
                    <th>Total</th>
                    <th>Demandé par</th>
                  </tr>
                </thead>
                <tbody>
                  {locationRequests.map((request, index) => (
                    <tr key={request._id}>
                      <td>{request.productName}</td>
                      <td>{request.quantity} {request.unit}</td>
                      <td>{request.price ? `${request.price.toFixed(2)} DT` : 'N/A'}</td>
                      <td>{request.price ? `${(request.price * request.quantity).toFixed(2)} DT` : 'N/A'}</td>
                      <td>{request.requestedBy}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr>
                    <td colSpan="3" className="print-total-label">Total {location}:</td>
                    <td colSpan="2" className="print-total-value">
                      {locationRequests.reduce((total, req) => total + (req.price || 0) * req.quantity, 0).toFixed(2)} DT
                    </td>
                  </tr>
                </tfoot>
              </table>
              
              <div className="print-footer">
                <div className="print-signature">
                  <div>Signature: ___________________</div>
                  <div>Date: ___________________</div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Interface normale (cachée à l'impression) */}
      <div className="screen-section">
        <div className="purchase-header">
          <h2>Liste des Demandes {userLocation && `- ${userLocation}`}</h2>
          <div className="header-actions">
            <div className="total-cost">
              Total: <span>{totalCost.toFixed(2)} DT</span>
            </div>
            <button 
              className="clear-filters-btn"
              onClick={clearFilters}
              disabled={!statusFilter && !locationFilter && !dateFilter.startDate && !dateFilter.endDate}
            >
              Effacer les Filtres
            </button>
          </div>
        </div>

        {/* Statistiques des statuts */}
        <div className="status-stats">
          <div className="stat-item">
            <span className="stat-label">Total</span>
            <span className="stat-value total">{statusStats.TOTAL}</span>
          </div>
          <div className="stat-item">
            <span className="stat-label">En Attente</span>
            <span className="stat-value pending">{statusStats.PENDING}</span>
          </div>
          <div className="stat-item">
            <span className="stat-label">Approuvées</span>
            <span className="stat-value approved">{statusStats.APPROVED}</span>
          </div>
          <div className="stat-item">
            <span className="stat-label">Rejetées</span>
            <span className="stat-value rejected">{statusStats.REJECTED}</span>
          </div>
          <div className="stat-item">
            <span className="stat-label">Confirmées</span>
            <span className="stat-value confirmed">{statusStats.CONFIRMED}</span>
          </div>
        </div>

        {/* Filtres en ligne compacte */}
        <div className="filters-compact">
          <div className="filter-group">
            <label className="filter-label">Statut</label>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="filter-select"
            >
              <option value="">Tous les statuts</option>
              <option value="PENDING">En Attente</option>
              <option value="APPROVED">Approuvées</option>
              <option value="REJECTED">Rejetées</option>
              <option value="CONFIRMED">Confirmées</option>
            </select>
          </div>

          <div className="filter-group">
            <label className="filter-label">Localisation</label>
            <select
              value={locationFilter}
              onChange={(e) => setLocationFilter(e.target.value)}
              className="filter-select"
            >
              <option value="">Toutes</option>
              {locations.map(location => (
                <option key={location._id} value={location.name}>
                  {location.name}
                </option>
              ))}
            </select>
          </div>

          <div className="filter-group date-group">
            <label className="filter-label">Période</label>
            <div className="date-inputs">
              <input
                type="date"
                value={dateFilter.startDate}
                onChange={(e) => setDateFilter(prev => ({ ...prev, startDate: e.target.value }))}
                className="date-input"
                placeholder="Date début"
              />
              <span className="date-separator">au</span>
              <input
                type="date"
                value={dateFilter.endDate}
                onChange={(e) => setDateFilter(prev => ({ ...prev, endDate: e.target.value }))}
                className="date-input"
                placeholder="Date fin"
              />
            </div>
          </div>
        </div>

        {/* Résumé des filtres */}
        {(statusFilter || locationFilter || dateFilter.startDate || dateFilter.endDate) && (
          <div className="filters-summary">
            <span className="summary-label">Filtres actifs:</span>
            {statusFilter && (
              <span className="filter-tag">
                Statut: {getStatusLabel(statusFilter)}
              </span>
            )}
            {locationFilter && (
              <span className="filter-tag">
                Localisation: {locationFilter}
              </span>
            )}
            {dateFilter.startDate && (
              <span className="filter-tag">
                Du: {formatDateForInput(dateFilter.startDate)}
              </span>
            )}
            {dateFilter.endDate && (
              <span className="filter-tag">
                Au: {formatDateForInput(dateFilter.endDate)}
              </span>
            )}
          </div>
        )}
        
        {/* Tableau avec hauteur fixe pour 5 lignes */}
        <div className="table-container-large">
          <table className="purchase-table">
            <thead>
              <tr>
                <th className="product-col">Produit</th>
                <th className="quantity-col">Quantité</th>
                <th className="price-col">Prix Unitaire</th>
                <th className="total-col">Total</th>
                <th className="status-col">Statut</th>
                <th className="date-col">Date Demande</th>
                <th className="date-col">Date Contrôle</th>
                <th className="date-col">Date Admin</th>
                {!userLocation && (
                  <th className="location-col">Localisation</th>
                )}
                <th className="comments-col">Commentaires</th>
              </tr>
            </thead>
            <tbody>
              {filteredRequests.length > 0 ? (
                filteredRequests.map(request => (
                  <tr key={request._id}>
                    <td className="product-cell">
                      <div className="product-name">{request.productName}</div>
                      <div className="product-requester">Par: {request.requestedBy}</div>
                    </td>
                    <td className="quantity-cell">
                      {request.quantity} {request.unit}
                    </td>
                    <td className="price-cell">
                      {request.price ? `${request.price.toFixed(2)} DT` : 'N/A'}
                    </td>
                    <td className="total-cell">
                      {request.price ? `${(request.price * request.quantity).toFixed(2)} DT` : 'N/A'}
                    </td>
                    <td className="status-cell">
                      <span className={`status-badge ${getStatusClass(request.status)}`}>
                        {getStatusLabel(request.status)}
                      </span>
                    </td>
                    <td className="date-cell">
                      {formatDate(request.createdAt)}
                    </td>
                    <td className="date-cell">
                      {formatDate(getControllerDate(request))}
                      {request.approvedBy && (
                        <div className="approver-name">Par: {request.approvedBy}</div>
                      )}
                    </td>
                    <td className="date-cell">
                      {formatDate(getAdminDate(request))}
                      {request.confirmedBy && (
                        <div className="approver-name">Par: {request.confirmedBy}</div>
                      )}
                    </td>
                    {!userLocation && (
                      <td className="location-cell">
                        {request.location}
                      </td>
                    )}
                    <td className="comments-cell">
                      <button 
                        className="comments-btn"
                        onClick={() => openCommentsModal(request)}
                        title="Voir les commentaires"
                      >
                        📝
                      </button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={userLocation ? 9 : 10} className="no-data">
                    Aucune demande trouvée avec les filtres actuels
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <div className="action-buttons">
          <div className="results-count">
            {filteredRequests.length} demande(s) trouvée(s)
          </div>
          {/* <div className="action-buttons-right">
            <button 
              className="print-btn"
              onClick={preparePrint}
            >
              Imprimer les Demandes Approuvées
            </button>
            {statusFilter === 'CONFIRMED' && filteredRequests.length > 0 && (
              <button className="purchase-btn">
                Procéder à l'Achat
              </button>
            )}
          </div> */}
        </div>
      </div>
    </div>
  );
};

export default PurchaseList;